import React from 'react'
import AdvancedOptionsHOC from 'utils/hoc/AdvancedOptionsHOC'
import { Button, Container } from 'components/UIElements'
import { browserHistory } from 'utils/navigation'
import PropTypes from 'prop-types'
import { DEPLOYMENT_KEYS, GEOFENCEABLE_TASKS, GROUP_TYPE_MAP } from 'utils/constants'
import { collectInstrumentValidationMessages } from 'utils/instrumentValidation'
import DeploymentSchedule from './DeploymentSchedule'
import DeploymentOptions from './DeploymentOptions'
import TimelineDisplay from './TimelineDisplay'
import GeofenceCheckbox from './GeofenceCheckbox'
import LocalTimePrompt from './LocalTimePrompt'
import 'react-datepicker/dist/react-datepicker.css'
import '../stylesheets/Scheduler.scss'

class SchedulerPage extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      isSendButtonDisabled: false,
    }
  }

  componentDidMount() {
    // Prefetch the Instruments list route chunk so redirect after deploy feels instant.
    // This app uses webpack `require.ensure` with chunkName `'instruments'` in `InstrumentsPage/index.js`.
    try {
      if (typeof require !== 'undefined' && typeof require.ensure === 'function') {
        require.ensure(
          [],
          require => {
            require('../../../../InstrumentsPage/modules/Instruments')
            require('../../../../InstrumentsPage/containers/InstrumentsViewContainer')
          },
          'instruments',
        )
      }
    } catch (e) {
      // no-op: prefetch is best-effort only
    }

    const { instrument, initializeCheckedCohorts, initializeCheckedSites } = this.props
    const { schedule = {} } = instrument.metadata || {}
    const { cohort = {} } = schedule
    const { filter = {}, type: cohortType } = cohort

    // Initialize checked cohorts/sites from stored filters, regardless of cohort type,
    // so audience types (all_participants/all_caregivers/all_users) still preload filters.
    if (Array.isArray(filter.include) && filter.include.length > 0) {
      initializeCheckedCohorts(filter.include)
    }
    if (Array.isArray(filter.site_ids) && filter.site_ids.length > 0) {
      initializeCheckedSites(filter.site_ids)
    }
  }

  componentDidUpdate(prevProps) {
    const { studyID, scoreErrors, instrument, studyVersionID } = this.props
    const { id } = instrument
    if (prevProps.scoreErrors !== scoreErrors) {
      if (scoreErrors) browserHistory.push(`/studies/${studyID}/${studyVersionID}/instruments/${id}/scoring`)
    }
    return null
  }

  componentWillUnmount() {
    const { resetCheckedSites } = this.props
    resetCheckedSites()
  }

  toggleDisabledState = () => {
    this.setState(state => ({
      isSendButtonDisabled: !state.isSendButtonDisabled,
    }))
  }

  onSend = () => {
    const { reviewSchedule, advancedOptions, showAdvancedOption, isReadOnlyState, studyID, studyVersionID, instrument } =
      this.props
    if (isReadOnlyState) {
      browserHistory.push(`/studies/${studyID}/${studyVersionID}/instruments/${instrument.id}/schedules`)
      return
    }
    this.toggleDisabledState()
    reviewSchedule({ onFail: this.toggleDisabledState }, showAdvancedOption ? advancedOptions : null)
  }

  render() {
    const {
      canSetDeploymentByVisit,
      canSetGeofencedInstruments,
      clearInstrumentTriggers,
      deleteMetadataField,
      errors,
      hasVisitSupport,
      instrument,
      instruments,
      instrumentForm,
      isClinro,
      isDiary,
      isGeofenceStudy,
      removeChainDeploymentInfo,
      hasStudyPromotion,
      studyID,
      studyVersionStatus,
      resetSchedule,
      updateChainDeployInfo,
      updateCohort,
      updateDeploy,
      updateExpire,
      updateGeofenceEnabled,
      updateInstrumentRoles,
      updateMetadataField,
      updateRecurring,
      study,
      userId,
      isReadOnlyState,
      studyVersionID,
      confirmedSchedules,
    } = this.props

    const { isSendButtonDisabled } = this.state

    const { config } = study
    const _config = config || {}
    const { custom_geofence_behavior } = _config
    const hasCustomGeofenceBehavior = !!custom_geofence_behavior
    const isGeofencingChecked = !!instrument.is_geofencing_enabled
    const hasForcedPriorityEnabled = study?.config?.forced_instrument_priority || study.forced_instrument_priority
    const { metadata } = instrument
    const { schedule = {}, visibility, module } = metadata
    const { chain_deployment_info } = schedule
    const isDeployedByVisit = chain_deployment_info?.[DEPLOYMENT_KEYS.chainDeployByVisit]

    // Only the covid and ecoli chatbots can be geofenced
    const canBeGeofenced = GEOFENCEABLE_TASKS.includes(module)
    const shouldRenderGeofenceCheckbox =
      canBeGeofenced && isGeofenceStudy && canSetGeofencedInstruments && hasCustomGeofenceBehavior
    const validationMessages = collectInstrumentValidationMessages(errors)

    return (
      <div className='scheduler page'>
        <h3>Deployment Schedule</h3>
        <h4 className='instrument-title'>{instrument.title}</h4>
        {validationMessages.length > 0 && (
          <div className='scheduler-validation-summary' role='alert'>
            <p>Please correct the following:</p>
            <ul>
              {validationMessages.map((message, index) => (
                <li key={`${index}-${message}`}>{message}</li>
              ))}
            </ul>
          </div>
        )}
        <LocalTimePrompt disabled={isGeofencingChecked} study={study} userId={userId} isDiary={isDiary} />
        {shouldRenderGeofenceCheckbox && (
          <GeofenceCheckbox
            isChecked={isGeofencingChecked}
            onClick={nextState => {
              resetSchedule()
              if (nextState) {
                updateRecurring({ scheme: 'persistent' })
              }
              updateGeofenceEnabled(nextState)
            }}
          />
        )}
        <Container>
          <>
            <DeploymentSchedule
              canSetDeploymentByVisit={canSetDeploymentByVisit}
              clearInstrumentTriggers={clearInstrumentTriggers}
              deleteMetadataField={deleteMetadataField}
              disabled={isGeofencingChecked || isReadOnlyState}
              errors={errors}
              hasVisitSupport={hasVisitSupport}
              instrumentId={instrument.id}
              instruments={instruments}
              instrumentForm={instrumentForm}
              instrumentRoles={instrument.instrument_roles}
              isClinro={isClinro}
              hasStudyPromotion={hasStudyPromotion}
              studyVersionStatus={studyVersionStatus}
              isDiary={isDiary}
              removeChainDeploymentInfo={removeChainDeploymentInfo}
              schedule={schedule}
              updateChainDeployInfo={updateChainDeployInfo}
              updateCohort={updateCohort}
              updateDeploy={updateDeploy}
              updateExpire={updateExpire}
              updateInstrumentRoles={updateInstrumentRoles}
              updateRecurring={updateRecurring}
              updateMetadataField={updateMetadataField}
              config={_config}
              userId={userId}
              study={study}
            />
          </>
          <DeploymentOptions
            hasForcedPriorityEnabled={hasForcedPriorityEnabled}
            schedule={schedule}
            visibility={visibility}
            userId={userId}
            study={study}
            {...this.props}
            disabled={isGeofencingChecked || isReadOnlyState}
          />
        </Container>
        {/* TODO: Implement timeline for triggered by visit */}
        <TimelineDisplay
          disabled={isGeofencingChecked}
          schedule={schedule}
          userId={userId}
          study={study}
          isDiary={isDiary}
          isDeployedByVisit={isDeployedByVisit}
        />
        <div className='button-list'>
          <Button
            grey
            id='cancel'
            content='Cancel'
            onClick={() => {
              if (clearInstrumentTriggers) {
                clearInstrumentTriggers({ needsHydration: true })
              }
              if (confirmedSchedules?.length) {
                browserHistory.push(
                  `/studies/${studyID}/${studyVersionID}/instruments/${instrument.id}/schedules`,
                )
              } else {
                browserHistory.push(`/studies/${studyID}/${studyVersionID}/instruments`)
              }
            }}
          />
          <Button
            id='send'
            className='deploy-button'
            content='Review Schedule'
            onClick={this.onSend}
            disabled={!isReadOnlyState && isSendButtonDisabled}
          />
        </div>
      </div>
    )
  }
}

SchedulerPage.propTypes = {
  advancedOptions: PropTypes.shape(),
  canSetDeploymentByVisit: PropTypes.bool,
  canSetGeofencedInstruments: PropTypes.bool,
  clearInstrumentTriggers: PropTypes.func,
  reviewSchedule: PropTypes.func,
  hasVisitSupport: PropTypes.bool,
  hasStudyPromotion: PropTypes.bool,
  initializeCheckedCohorts: PropTypes.func,
  initializeCheckedSites: PropTypes.func,
  instrument: PropTypes.shape({
    id: PropTypes.string,
    is_geofencing_enabled: PropTypes.bool,
    instrument_roles: PropTypes.arrayOf(PropTypes.number),
    title: PropTypes.string,
    metadata: PropTypes.shape({ schedule: PropTypes.object, deploy: PropTypes.object }),
  }),
  instrumentForm: PropTypes.shape({
    metadata: PropTypes.shape({ schedule: PropTypes.object, deploy: PropTypes.object }),
  }),
  instruments: PropTypes.arrayOf(PropTypes.array),
  isClinro: PropTypes.bool,
  isDiary: PropTypes.bool,
  isGeofenceStudy: PropTypes.bool,
  removeChainDeploymentInfo: PropTypes.func,
  resetCheckedSites: PropTypes.func,
  resetSchedule: PropTypes.func,
  scoreErrors: PropTypes.objectOf(PropTypes.object),
  siteID: PropTypes.number,
  study: PropTypes.shape({
    name: PropTypes.string,
    config: PropTypes.oneOfType([PropTypes.object]),
    forced_instrument_priority: PropTypes.bool,
  }),
  studyID: PropTypes.number,
  studyVersionID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  confirmedSchedules: PropTypes.arrayOf(PropTypes.object),
  errors: PropTypes.object,
  studyVersionStatus: PropTypes.string,
  triggersLoading: PropTypes.bool,
  updateChainDeployInfo: PropTypes.func,
  updateCohort: PropTypes.func,
  updateDeploy: PropTypes.func,
  updateExpire: PropTypes.func,
  updateGeofenceEnabled: PropTypes.func,
  updateInstrumentRoles: PropTypes.func,
  updateRecurring: PropTypes.func,
  showAdvancedOption: PropTypes.bool,
}

export default AdvancedOptionsHOC(SchedulerPage)
